<template>
  <div :class="elClass">{{ formattedTime }}</div>
</template>

<script setup>
import moment from 'moment';
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { formatTime } from '@/helpers/time.js';
import { isPositiveNumber } from '@/helpers/common.js';

const emits = defineEmits(['time:expired']);

const props = defineProps({
  endDate: {
    type: [Date, String, Number],
    default: 0,
  },
  startImmediate: {
    type: Boolean,
    default: true,
  },
  timerFormat: {
    type: String,
    default: 'mm:ss',
    validator: (value) => ['hh:mm:ss', 'mm:ss'].includes(value),
  },
  elClass: {
    type: String,
    default: '',
  },
});

const timerInSeconds = ref(0);
const timerIntervalId = ref(null);
const timerIntervalDelay = 1000;

const showHours = computed(() => props.timerFormat === 'hh:mm:ss');

const formattedTime = computed(() =>
  formatTime(timerInSeconds.value, '', { showHours: showHours.value }),
);

const intervalCleanup = () => {
  if (timerIntervalId.value) {
    clearInterval(timerIntervalId.value);
    timerIntervalId.value = null;
  }
};

const startTimer = () => {
  const end = moment(props.endDate);

  if (!end.isValid() || end.isBefore(moment())) {
    emits('time:expired');

    return;
  }

  const updateTimer = () => {
    const now = moment();

    const remainingTime = Math.ceil(moment.duration(end.diff(now)).asSeconds());

    if (isPositiveNumber(remainingTime)) {
      timerInSeconds.value = remainingTime;
    } else {
      intervalCleanup();
      timerInSeconds.value = 0;

      emits('time:expired');
    }
  };

  updateTimer();

  timerIntervalId.value = setInterval(updateTimer, timerIntervalDelay);
};

const stopTimer = () => {
  intervalCleanup();
  timerInSeconds.value = 0;
};

onMounted(() => {
  if (props.startImmediate) {
    nextTick(() => {
      startTimer();
    });
  }
});

onBeforeUnmount(intervalCleanup);

defineExpose({
  startTimer,
  stopTimer,
});
</script>
